/**
 * S17. The live log tail.
 *
 * Screen spec cut this route and reserved it, on the argument that S9's build
 * log already proves the log rendering component. That argument holds for a
 * log that has finished. It does not hold for a log that is still arriving,
 * which is a different problem with three requirements a static fence never
 * has: it has to say that more is coming, it has to let a person stop it long
 * enough to read a line, and it has to be honest about how much of it is kept.
 * So this screen exists, and it is deliberately still not in the rail: it is
 * reachable from the Advanced layer of the activity page and by its address,
 * and a person who has not asked for a technical view never meets it.
 *
 * THE CARET IS THE ONE SANCTIONED LOOP IN THE PRODUCT, and the doctrine rule
 * it spends is "at most one thing moving per screen". Nothing else here moves:
 * no spinner, no pulsing dot, no animated bar. The caret marks where the next
 * line will land, and it stops the moment the stream is paused, which is the
 * only reason a blinking rule is allowed on a surface whose brand explicitly
 * refuses the terminal costume. See LogStream for the full argument.
 *
 * CLOCK DERIVED, LIKE EVERYTHING ELSE. The tail is a pure function of
 * (now - origin) * speed against the fixture's own gaps, so pausing is
 * arithmetic on the origin rather than a queue that has to be drained, and a
 * backgrounded tab comes back correct instead of behind. The engine's own
 * ticker is bound to timeline liveness and a tail is not a timeline, so this
 * screen schedules its repaints with useWallClock. The discipline is identical.
 *
 * THE CAP IS A NORMAL CONDITION, NOT AN ERROR. The well keeps the last 200
 * lines and says so, in the same flat voice the row cap uses on the database
 * grid. A product that hid the cap would be a product a person discovered it
 * from at the worst possible moment.
 *
 * THE WHOLE SCREEN IS THE ADVANCED LAYER, so it has a Simple gate rather than
 * a disclosure: there is no Simple half of a live log to append to. Deep
 * linking here in Simple is a real thing a reviewer will do, and an empty page
 * would be the wrong answer, so the page says where the switch is, offers it,
 * and points at the activity feed, which is the same information in sentences.
 * The query runner answers the same problem the same way, in the same words.
 *
 * FAKEABLE STATES, deep linkable per prototype mechanic (a):
 *   ?state=paused   the stream stopped, the caret out
 *   ?state=quiet    an app nobody has visited, which is not a broken log
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { LogStream } from '../../components/patterns/LogStream';
import type { StreamLine } from '../../components/patterns/LogStream';
import { Notice } from '../../components/patterns/Notice';
import { Skeleton } from '../../components/patterns/Skeleton';

import { environmentOf, getProject } from '../../lib/api';
import { count } from '../../lib/format';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { useWallClock } from '../../lib/hooks/use-wall-clock';
import { t } from '../../lib/i18n/t';
import { liveLogLines } from '../../lib/mock/seed';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { LiveLogLine, Speed } from '../../lib/types';

import '../../styles/components/log-tail.css';
// The ADVANCED chip is one treatment in one place. This screen borrows the
// disclosure's rather than drawing a fourth copy of the same 20px box.
import '../../styles/components/disclosure.css';

const STATES = ['live', 'paused', 'quiet'] as const;
type StateName = (typeof STATES)[number];

/** The well keeps this many lines. Stated on screen, never discovered. */
const BUFFER = 200;

type Filter = 'all' | 'app' | 'request' | 'error';

const FILTERS: Filter[] = ['all', 'app', 'request', 'error'];

/**
 * Instant is a timeline control: it jumps a run to its end. A tail has no end,
 * so it reads instant as the fastest speed the dock offers rather than
 * printing an infinity of lines in one frame.
 */
function factorOf(speed: Speed): number {
  return speed === 'instant' ? 8 : speed;
}

/** HH:MM:SS. A machine stamp, never localized, always tabular. */
function clockStamp(ms: number): string {
  const at = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`;
}

/**
 * The tail, computed rather than accumulated.
 *
 * The pool cycles, so the stream runs for as long as somebody watches without
 * the fixture being a thousand lines long, and every line's stamp is derived
 * from the origin, which is what makes the column of times read as one clock.
 */
function tailAt(pool: LiveLogLine[], origin: number, now: number, factor: number): StreamLine[] {
  if (pool.length === 0) return [];

  const prefix: number[] = [];
  let sum = 0;
  for (const line of pool) {
    sum += line.gapMs;
    prefix.push(sum);
  }
  const cycleMs = sum;

  const elapsed = Math.max(0, (now - origin) * factor);
  const cycles = Math.floor(elapsed / cycleMs);
  const rest = elapsed % cycleMs;
  const within = prefix.filter((at) => at <= rest).length;
  const emitted = cycles * pool.length + within;

  const out: StreamLine[] = [];
  for (let i = Math.max(0, emitted - BUFFER); i < emitted; i += 1) {
    const slot = i % pool.length;
    const cycle = Math.floor(i / pool.length);
    const at = origin + (cycle * cycleMs + prefix[slot]) / factor;
    out.push({
      id: `line-${i}`,
      at: clockStamp(at),
      level: pool[slot].level,
      text: pool[slot].text,
    });
  }
  return out;
}

/* ------------------------------------------------------------------ screen */

export default function LogTail() {
  const slug = useProjectSlug() ?? '';
  const scenarioId = useDemoStore((s) => s.scenarioId);
  const speed = useDemoStore((s) => s.prefs.speed);
  const { state } = useScreenState<StateName>(STATES, 'live');
  const [advanced, setAdvanced] = useAdvanced();

  const [origin, setOrigin] = useState(() => Date.now());
  const [pausedAt, setPausedAt] = useState<number | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  // The state parameter is the authority on arrival, so a screenshot link of
  // the paused screen opens paused rather than opening live and needing a
  // click. It re-arms whenever the link changes.
  useEffect(() => {
    setPausedAt(state === 'paused' ? Date.now() : null);
    setOrigin(Date.now() - 12_000);
  }, [state, slug]);

  const clock = useWallClock(pausedAt === null);
  const now = pausedAt ?? clock;

  const { data, isLoading } = useAsync(async () => {
    const project = await getProject(slug);
    return project;
  }, [slug, scenarioId]);

  const pool = useMemo(
    () => (state === 'quiet' ? [] : liveLogLines(slug)),
    [slug, state],
  );

  const lines = useMemo(
    () => tailAt(pool, origin, now, factorOf(speed)),
    [pool, origin, now, speed],
  );

  useEffect(() => {
    if (data && advanced) {
      captureEvent('log_tail_viewed', {
        project_slug: data.slug,
        filter,
        line_count: lines.length,
      });
    }
  }, [data, advanced, filter, lines.length]);

  // After every hook, and before anything that reads the tail: in Simple this
  // page has nothing to draw, and a blank screen would read as a broken route.
  if (!advanced) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Live logs')}</h1>
        </header>

        <Notice
          tone="quiet"
          title={t('Live logs are an Advanced detail.')}
          actions={
            <Button variant="solid" onClick={() => setAdvanced(true)}>
              {t('Turn on Advanced')}
            </Button>
          }
        >
          <p>{t('Advanced adds technical details. It never hides anything.')}</p>
          <p>
            {t('Everything here is on your activity page too, in plain language.')}{' '}
            <Link className="link-inline" to={`/p/${encodeURIComponent(slug)}/activity`}>
              {t('Activity')}
            </Link>
          </p>
        </Notice>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Live logs')}</h1>
        </header>
        <Skeleton shape="panel" count={1} label={t('Connecting to your app')} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Live logs')}</h1>
        </header>
        <Notice tone="quiet" title={t('That project is not here.')}>
          <p>{t('It may have been deleted, or the address may have a typo in it.')}</p>
        </Notice>
      </div>
    );
  }

  const project = data;
  const base = `/p/${encodeURIComponent(project.slug)}`;
  const paused = pausedAt !== null;
  const address = environmentOf(project).webAddress.replace(/^https?:\/\//, '');

  const counts: Record<Filter, number> = {
    all: lines.length,
    app: lines.filter((line) => line.level === 'app').length,
    request: lines.filter((line) => line.level === 'request').length,
    error: lines.filter((line) => line.level === 'error').length,
  };

  const shown = filter === 'all' ? lines : lines.filter((line) => line.level === filter);

  const filterWord: Record<Filter, string> = {
    all: t('Everything'),
    app: t('Your app'),
    request: t('Requests'),
    error: t('Errors'),
  };

  function togglePause() {
    if (pausedAt === null) {
      setPausedAt(Date.now());
      return;
    }
    // Resuming moves the origin forward by the length of the pause, so the
    // stream carries on from the line it stopped at instead of jumping over
    // everything that would have arrived while nobody was watching.
    setOrigin((was) => was + (Date.now() - pausedAt));
    setPausedAt(null);
  }

  const quiet =
    project.status === 'paused'
      ? t('This project is paused, so nothing is running and nothing is being printed.')
      : t('Nothing has come through yet. Lines appear here the first time somebody opens your app.');

  return (
    <div className="page">
      <header className="page__head">
        <div className="logt__title-row">
          <h1 className="page__title">{t('Live logs')}</h1>
          <span className="disc__chip">{t('ADVANCED')}</span>
        </div>
        <p className="page__lede">
          {t("Your app's own words, as they happen, from {address}.", { address })}
        </p>
        <p className="logt__aside">
          {t('Everything here is on your activity page too, in plain language.')}{' '}
          <Link className="link-inline" to={`${base}/activity`}>
            {t('Activity')}
          </Link>
        </p>
      </header>

      <div className="logt__bar">
        <div className="logt__chips" role="group" aria-label={t('Show')}>
          {FILTERS.map((value) => (
            <button
              key={value}
              type="button"
              className="logt__chipbtn"
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              <span className="logt__chipword">{filterWord[value]}</span>
              <span className="logt__chipcount mono">{count(counts[value])}</span>
            </button>
          ))}
        </div>

        <div className="logt__right">
          {/* The state in words, and it is the announced one. The caret says
              the same thing visually, and a person who cannot see the caret
              gets the sentence instead of nothing. */}
          <span className="logt__state" role="status" aria-live="polite">
            {paused ? t('Paused') : t('Streaming')}
          </span>
          <Button variant="solid" onClick={togglePause}>
            {paused ? t('Start again') : t('Pause')}
          </Button>
        </div>
      </div>

      <LogStream
        label={t('Live logs')}
        lines={shown}
        live={!paused}
        follow={!paused}
        quiet={quiet}
      />

      <p className="logt__cap">
        {/* The cap is a normal condition, stated in the same flat voice the
            database grid uses for its row cap. Nothing here is an error. */}
        {t('Showing the last {n} lines.', { n: count(BUFFER) })}{' '}
        {/* shown.length is a live filter count, so one and none are both
            ordinary readings of this line. The verb has to agree, and none is
            worth its own sentence rather than a zero in front of a plural. */}
        {filter === 'all'
          ? null
          : shown.length === 0
            ? t('None of them match what you picked.')
            : shown.length === 1
              ? t('1 of them matches what you picked.')
              : t('{n} of them match what you picked.', { n: count(shown.length) })}
      </p>
    </div>
  );
}
